/**
 * Gmail Statement Sync — consumer page.
 *
 * States:
 *   1. NOT CONNECTED — Connect Gmail CTA (kicks the OAuth flow).
 *   2. CONNECTED, NO DRAFTS — show status + "Sync now" button.
 *   3. CONNECTED, DRAFTS PENDING — paginated list of parsed transactions;
 *      each row has Edit-in-place fields + Confirm / Discard actions.
 *
 * The page deliberately does NOT auto-apply drafts — every transaction is
 * surfaced for user review so we never persist a hallucinated row.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mail, Loader2, RefreshCw, Link2, Link2Off, Sparkles, Check, X, ShieldCheck,
  AlertTriangle, ExternalLink, Inbox, Calendar, IndianRupee, Hash, Lock, LogIn,
  Trash2, Copy,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { CategoryCombobox } from '@/components/forms/CategoryCombobox';
import { Gmail as GmailApi, Cards as CardsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR, formatDate, formatRelative, formatDateTime } from '@/lib/format';
import { CATEGORIES, CATEGORY_GROUPS, normaliseCategory } from '@/lib/categories';
import { TXN_MODES, DEFAULT_MODE_VALUE, decomposeMode } from '@/lib/transactionModes';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const KIND_TONE = {
  purchase: 'bg-foreground/5 text-foreground border-border',
  refund:   'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  fee:      'bg-rose-500/10 text-rose-600 border-rose-500/30',
  interest: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  emi:      'bg-violet-500/10 text-violet-600 border-violet-500/30',
  payment:  'bg-sky-500/10 text-sky-600 border-sky-500/30',
};

const DraftRow = ({ draft, userCards, onApply, onDiscard, applying }) => {
  const isInternational = !!draft.original_currency && draft.original_currency !== 'INR';
  const initialModeValue = isInternational
    ? 'international'
    : (draft.kind === 'emi' ? 'emi' : DEFAULT_MODE_VALUE);

  const [edited, setEdited] = useState({
    amount: String(draft.amount_inr ?? ''),
    merchant: draft.merchant || '',
    // Phase 12.3 — normalise the GPT-5 category hint against the canonical
    // alias map. This avoids displaying values like "Food & Beverage" or
    // "Subscription" that the engine doesn't understand.
    // Phase 20 — normaliseCategory also coerces 'Online'/'Offline'/'EMI'/
    // 'International' (which are MODES, not categories) to 'General'. The
    // mode selector below captures those signals separately.
    category: normaliseCategory(draft.category_hint),
    date: draft.date_iso || new Date().toISOString().slice(0, 10),
    // Phase 20 — explicit Mode selector. Defaults from GPT-5 hints
    // (international flag, EMI kind) when available, else Online.
    mode_value: initialModeValue,
    user_card_id: draft.suggested_user_card_id || (userCards[0]?.id || ''),
  });

  const lowConf = (draft.confidence ?? 1) < 0.6;
  const isDuplicate = !!draft.potential_duplicate;
  const updates = (patch) => setEdited((s) => ({ ...s, ...patch }));

  const handleConfirm = () => {
    if (!edited.user_card_id) {
      toast.error('Pick a card to apply this transaction to.');
      return;
    }
    if (!edited.amount || Number(edited.amount) <= 0) {
      toast.error('Amount must be a positive number.');
      return;
    }
    // Phase 20 — decompose UI mode into the storage triple.
    const { mode, is_emi, is_international } = decomposeMode(edited.mode_value);
    onApply({
      draft_id: draft.id,
      user_card_id: edited.user_card_id,
      amount_inr: Number(edited.amount),
      merchant: edited.merchant.trim() || 'Unknown',
      category: edited.category,
      mcc: draft.mcc_hint || null,
      date_iso: edited.date,
      mode,
      is_emi,
      is_international,
      txn_at: draft.txn_time || draft.source_date || null,
    });
  };

  return (
    <Card
      className={cn(
        'p-4',
        isDuplicate && 'border-amber-400/50 bg-amber-500/5',
      )}
      data-testid={`gmail-draft-${draft.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={cn(
            'h-9 w-9 rounded-lg grid place-items-center shrink-0',
            KIND_TONE[draft.kind] || KIND_TONE.purchase,
          )}>
            <IndianRupee className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold tracking-tight truncate">
                {draft.merchant || 'Unknown merchant'}
              </span>
              <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider', KIND_TONE[draft.kind] || KIND_TONE.purchase)}>
                {draft.kind}
              </Badge>
              {lowConf && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-700 border-amber-500/30 gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Verify
                </Badge>
              )}
              {/* Phase 20 — Potential duplicate pill. Hover for the matched
                  record details. Soft duplicates are IMPORTED but flagged
                  so the user resolves ambiguity (e.g. auth + settled emails
                  for the same charge, or two genuine same-hour purchases). */}
              {isDuplicate && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        data-testid={`gmail-draft-duplicate-${draft.id}`}
                        className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-700 border-amber-500/40 gap-1"
                      >
                        <Copy className="h-2.5 w-2.5" />
                        Potential duplicate
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px] text-xs leading-snug">
                      <div className="font-medium mb-1">Possibly a duplicate</div>
                      {draft.duplicate_of ? (
                        <div className="text-muted-foreground">
                          Same amount + card + merchant + hour as an existing{' '}
                          {draft.duplicate_of.kind || 'transaction'}
                          {draft.duplicate_of.date ? ` from ${draft.duplicate_of.date}` : ''}.
                          Review and apply only if it's a separate charge.
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          Same amount + card + merchant + hour as an existing entry.
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {isInternational && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  {draft.original_currency} {draft.original_amount}
                </Badge>
              )}
            </div>
            {(draft.source_subject || draft.source_from) && (
              <div className="mt-0.5 text-[11px] text-muted-foreground truncate" title={draft.source_subject}>
                <Mail className="h-3 w-3 inline mr-1 -mt-px" />
                {draft.source_subject || draft.source_from}
              </div>
            )}
            {/* Phase 20 — show the transaction date+time prominently so
                users can disambiguate same-day same-amount charges. Falls
                back to the parsed date when no timestamp was captured. */}
            <div className="mt-1 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3 inline mr-1 -mt-px" />
              {draft.txn_time
                ? formatDateTime(draft.txn_time)
                : (draft.source_date ? formatDateTime(draft.source_date) : (draft.date_iso || ''))}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-lg font-semibold font-mono-num">{formatINR(draft.amount_inr)}</div>
          {draft.confidence != null && (
            <div className="text-[10px] text-muted-foreground">conf {Math.round(draft.confidence * 100)}%</div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-2.5">
        <div className="space-y-1">
          <Label className="text-[11px]">Amount (₹)</Label>
          <Input
            value={edited.amount}
            onChange={(e) => updates({ amount: e.target.value.replace(/[^0-9.]/g, '') })}
            inputMode="decimal"
            className="h-9 font-mono-num"
            data-testid={`gmail-draft-amount-${draft.id}`}
          />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-[11px]">Merchant</Label>
          <Input
            value={edited.merchant}
            onChange={(e) => updates({ merchant: e.target.value })}
            className="h-9"
            data-testid={`gmail-draft-merchant-${draft.id}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Category</Label>
          <CategoryCombobox
            value={edited.category}
            onChange={(v) => updates({ category: v })}
            suggestions={CATEGORIES}
            placeholder="Pick or type a category"
            testid={`gmail-draft-category-${draft.id}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Date</Label>
          <Input
            type="date"
            value={edited.date}
            onChange={(e) => updates({ date: e.target.value })}
            className="h-9 font-mono-num"
            data-testid={`gmail-draft-date-${draft.id}`}
          />
        </div>
      </div>

      {/* Phase 20 — Mode selector spans full width below the main grid so
          users can pick Online / Offline / EMI / International / Other
          independently of the merchant category. */}
      <div className="mt-3 space-y-1">
        <Label className="text-[11px]">Mode</Label>
        <Select value={edited.mode_value} onValueChange={(v) => updates({ mode_value: v })}>
          <SelectTrigger className="h-9" data-testid={`gmail-draft-mode-${draft.id}`}>
            <SelectValue placeholder="Pick a mode" />
          </SelectTrigger>
          <SelectContent>
            {TXN_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value} data-testid={`gmail-draft-mode-${draft.id}-${m.value}`}>
                <div className="flex flex-col items-start">
                  <span className="text-sm">{m.label}</span>
                  <span className="text-[10px] text-muted-foreground line-clamp-1">{m.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-2.5 items-end">
        <div className="lg:col-span-2 space-y-1">
          <Label className="text-[11px]">Apply to card</Label>
          <Select value={edited.user_card_id} onValueChange={(v) => updates({ user_card_id: v })}>
            <SelectTrigger className="h-9" data-testid={`gmail-draft-card-${draft.id}`}>
              <SelectValue placeholder="Pick a card" />
            </SelectTrigger>
            <SelectContent>
              {userCards.length === 0 && (
                <SelectItem value="__none__" disabled>No cards added yet</SelectItem>
              )}
              {userCards.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.bank} — {c.name}{c.last4 ? ` ····${c.last4}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDiscard(draft.id)}
            disabled={applying}
            className="text-muted-foreground gap-1"
            data-testid={`gmail-draft-discard-${draft.id}`}
          >
            <X className="h-3.5 w-3.5" /> Discard
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={applying}
            className="gap-1"
            data-testid={`gmail-draft-apply-${draft.id}`}
          >
            <Check className="h-3.5 w-3.5" />
            Apply
          </Button>
        </div>
      </div>
    </Card>
  );
};


export default function GmailSync() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { user } = useAuth();
  const isDemo = !!user?.is_demo;
  const [status, setStatus] = useState(null);                  // { connected, gmail_address, ... }
  const [statusLoading, setStatusLoading] = useState(!isDemo); // demo never loads
  const [drafts, setDrafts] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // Phase 21 — store the last sync response so we can show a small
  // disclosure of `non_spend_events` (refunds/payments/fees skipped).
  const [lastSync, setLastSync] = useState(null);
  const [applying, setApplying] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(60);

  const loadStatus = useCallback(async () => {
    try {
      const s = await GmailApi.status();
      setStatus(s);
    } catch (e) {
      toast.error(e.message || 'Failed to load Gmail status');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const d = await GmailApi.drafts();
      setDrafts(Array.isArray(d) ? d : []);
    } catch (e) {
      // It's OK to silently fail here — empty drafts is the common case.
    }
  }, []);

  const loadUserCards = useCallback(async () => {
    try {
      const c = await CardsApi.mine();
      setUserCards(Array.isArray(c) ? c : (c?.cards || []));
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (isDemo) return;            // demo accounts never call /api/gmail/*
    loadStatus();
    loadDrafts();
    loadUserCards();
  }, [isDemo, loadStatus, loadDrafts, loadUserCards]);

  // Show a toast based on the redirect status from /api/oauth/gmail/callback.
  useEffect(() => {
    const s = search.get('status');
    if (s === 'connected') toast.success('Gmail connected!');
    if (s === 'error') {
      const reason = search.get('reason');
      toast.error(`Gmail connect failed${reason ? `: ${reason}` : ''}`);
    }
  }, [search]);

  const handleConnect = async () => {
    try {
      const { auth_url } = await GmailApi.loginUrl();
      if (!auth_url) throw new Error('No auth URL returned');
      window.location.href = auth_url;
    } catch (e) {
      toast.error(e.message || 'Could not start Gmail connect');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Gmail? We will revoke access and forget your tokens.')) return;
    try {
      await GmailApi.disconnect();
      toast.success('Gmail disconnected.');
      await loadStatus();
      setDrafts([]);
    } catch (e) {
      toast.error(e.message || 'Disconnect failed');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Phase 21 — scale `max_messages` to the chosen lookback window.
      // A 60-day window for an active inbox commonly has 80–150
      // transactional alerts; the previous hard-coded `30` cap silently
      // truncated older transactions. Backend enforces an absolute cap
      // of 500 via its own Pydantic validator, so we stay below that.
      const maxMessages = Math.min(500, Math.max(60, lookbackDays * 4));
      const res = await GmailApi.sync({ lookback_days: lookbackDays, max_messages: maxMessages });
      if (res?.drafts?.length) {
        const dupCount = res.soft_duplicates_flagged ?? 0;
        const nonSpendCount = (res.non_spend_events ?? []).length;
        const parts = [`Found ${res.drafts.length} new transaction${res.drafts.length === 1 ? '' : 's'}`];
        if (dupCount > 0) parts.push(`${dupCount} flagged as potential duplicate${dupCount === 1 ? '' : 's'}`);
        if (nonSpendCount > 0) parts.push(`${nonSpendCount} non-spend event${nonSpendCount === 1 ? '' : 's'} skipped`);
        toast.success(parts.join(' · '));
      } else {
        toast.info(`Scanned ${res?.messages_scanned ?? 0} emails — no new transactions to import.`);
      }
      // Phase 21 — surface non-spend events in state so the UI can
      // render a disclosure ("We also detected N refunds / payments
      // that aren't tracked as spend").
      setLastSync(res || null);
      await loadDrafts();
      await loadStatus();
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleApply = async (item) => {
    setApplying(true);
    try {
      const res = await GmailApi.apply([item]);
      if (res?.applied) {
        toast.success('Transaction added.');
        setDrafts((d) => d.filter((x) => x.id !== item.draft_id));
      } else {
        toast.error('Could not apply draft. Refreshing list…');
        await loadDrafts();
      }
    } catch (e) {
      toast.error(e.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleDiscard = async (draftId) => {
    try {
      await GmailApi.discard(draftId);
      setDrafts((d) => d.filter((x) => x.id !== draftId));
    } catch (e) {
      toast.error(e.message || 'Discard failed');
    }
  };

  const handleApplyAll = async () => {
    if (drafts.length === 0) return;
    if (userCards.length === 0) {
      toast.error('Add at least one card first so we know where to log these.');
      return;
    }
    setApplying(true);
    try {
      const items = drafts
        .filter((d) => d.suggested_user_card_id || userCards[0]?.id)
        .map((d) => {
          const isIntl = !!d.original_currency && d.original_currency !== 'INR';
          // Phase 20 — derive mode from the same hints DraftRow uses.
          const modeValue = isIntl
            ? 'international'
            : (d.kind === 'emi' ? 'emi' : DEFAULT_MODE_VALUE);
          const { mode, is_emi, is_international } = decomposeMode(modeValue);
          return {
            draft_id: d.id,
            user_card_id: d.suggested_user_card_id || userCards[0].id,
            amount_inr: Number(d.amount_inr),
            merchant: d.merchant || 'Unknown',
            // Phase 20 — normaliseCategory now coerces mode-labels
            // (Online/Offline/EMI/International) to 'General'.
            category: normaliseCategory(d.category_hint),
            mcc: d.mcc_hint || null,
            date_iso: d.date_iso || new Date().toISOString().slice(0, 10),
            mode,
            is_emi,
            is_international,
            txn_at: d.txn_time || d.source_date || null,
          };
        });
      const res = await GmailApi.apply(items);
      toast.success(`Applied ${res?.applied ?? 0} transactions.`);
      await loadDrafts();
    } catch (e) {
      toast.error(e.message || 'Apply-all failed');
    } finally {
      setApplying(false);
    }
  };

  /** Phase 20 — bulk dismiss every pending draft. Idempotent on the server. */
  const handleDiscardAll = async () => {
    if (drafts.length === 0) return;
    setApplying(true);
    try {
      const res = await GmailApi.discardAll();
      toast.success(`Dismissed ${res?.discarded ?? 0} draft(s).`);
      setDrafts([]);
      await loadDrafts();
    } catch (e) {
      toast.error(e.message || 'Dismiss-all failed');
    } finally {
      setApplying(false);
    }
  };

  // ───── UI ─────
  // Phase 11.1 — Demo accounts cannot connect personal Gmail. We render
  // a feature-locked screen instead of the connect CTA so the demo user
  // sees what the feature does, but is steered toward signing in.
  if (isDemo) {
    return (
      <div className="space-y-6" data-testid="gmail-sync-page-demo">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Signed-in only · Beta
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Gmail statement sync</h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              Connect your Gmail to auto-import credit-card transactions parsed from issuer emails.
            </p>
          </div>
        </div>

        <Card className="p-8 border border-dashed border-border" data-testid="gmail-sync-demo-locked">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
              <Mail className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-[280px] max-w-2xl">
              <div className="font-display text-xl font-semibold">
                Gmail Sync is available only for signed-in users.
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                The demo account is sandboxed and cannot connect personal accounts, third-party
                integrations, or billing flows. Create a free CardAssist Online account and your
                inbox is auto-scanned for credit-card transactions every time you click Sync.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-500/30 bg-emerald-500/5">
                  <ShieldCheck className="h-3 w-3" /> gmail.readonly only
                </Badge>
                <Badge variant="outline">Credit-card transactions only</Badge>
                <Badge variant="outline">Revocable anytime</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                size="lg"
                onClick={() => navigate('/signup')}
                className="gap-2 h-11"
                data-testid="gmail-sync-demo-signup-button"
              >
                <Sparkles className="h-4 w-4" /> Create free account
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 gap-2" data-testid="gmail-sync-demo-signin-link">
                <Link to="/login"><LogIn className="h-4 w-4" /> Sign in</Link>
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-5" data-testid="gmail-sync-demo-explainer">
          <h3 className="font-display text-base font-semibold tracking-tight mb-3">What you&rsquo;d get after signing in</h3>
          <ul className="text-sm text-foreground/85 space-y-2 list-disc list-inside">
            <li><span className="text-foreground">Auto-imported transactions</span> from your last 14–180 days of issuer emails (HDFC, Axis, ICICI, SBI, Amex, HSBC, Kotak, RBL, SCB, Yes Bank, IndusInd, AU Bank).</li>
            <li><span className="text-foreground">Credit-card only</span> — UPI, NEFT, IMPS, debit-card, net-banking and OTP emails are filtered out by a 4-layer defence before they ever reach you.</li>
            <li><span className="text-foreground">Review &amp; confirm</span> each parsed transaction (amount, merchant, category, card) before it&rsquo;s logged to your wallet.</li>
            <li><span className="text-foreground">Card matching</span> — drafts are auto-routed to the right card in your wallet using last-4 digits or product-name hints from the email.</li>
          </ul>
        </Card>
      </div>
    );
  }

  if (statusLoading) {
    return (
      <div className="grid place-items-center min-h-[60vh] text-muted-foreground" data-testid="gmail-sync-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gmail-sync-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-accent">
            <Sparkles className="h-3.5 w-3.5" /> Auto-sync · Beta
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Gmail statement sync</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Connect your Gmail and we&rsquo;ll scan recent bank notifications to log offline + non-browser transactions
            automatically. Every parsed transaction lands here as a <span className="text-foreground">draft</span> for you
            to confirm — nothing is saved until you click Apply.
          </p>
        </div>
        {status?.connected && (
          <Button
            variant="ghost"
            onClick={handleDisconnect}
            className="gap-1.5 text-muted-foreground"
            data-testid="gmail-disconnect-button"
          >
            <Link2Off className="h-4 w-4" /> Disconnect
          </Button>
        )}
      </div>

      {/* Connection card */}
      <Card className={cn(
        'p-5 border',
        status?.connected ? 'border-accent/30 bg-accent/5' : 'border-border bg-card',
      )}>
        {!status?.connected ? (
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-xl bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
              <Mail className="h-5 w-5 text-foreground/80" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="font-display text-lg font-semibold">Connect Gmail (read-only)</div>
              <div className="text-sm text-muted-foreground mt-1 max-w-xl">
                We use a read-only Gmail scope strictly to scan transaction notifications. We never read other
                emails, never store the body, and you can disconnect anytime.
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-500/30 bg-emerald-500/5">
                  <ShieldCheck className="h-3 w-3" /> gmail.readonly only
                </Badge>
                <Badge variant="outline">No mail is sent</Badge>
                <Badge variant="outline">Revocable anytime</Badge>
              </div>
            </div>
            <Button
              size="lg"
              onClick={handleConnect}
              className="gap-2 h-11"
              data-testid="gmail-connect-button"
            >
              <Link2 className="h-4 w-4" /> Connect Gmail
            </Button>
          </div>
        ) : (
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-xl bg-accent/15 grid place-items-center shrink-0 text-accent">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="font-display text-lg font-semibold">Gmail connected</div>
              <div className="text-sm text-muted-foreground mt-1">
                Inbox: <span className="text-foreground" data-testid="gmail-connected-address">{status.gmail_address || '—'}</span>
              </div>
              {status.last_sync_at && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Last sync {formatRelative(status.last_sync_at)} · {status.last_sync_status?.label || '—'} ·
                  {' '}{status.last_sync_status?.drafts ?? 0} drafts
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Look back</span>
                <Select value={String(lookbackDays)} onValueChange={(v) => setLookbackDays(Number(v))}>
                  <SelectTrigger className="h-9 w-[110px]" data-testid="gmail-lookback-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="gap-2"
                data-testid="gmail-sync-button"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {syncing ? 'Scanning…' : 'Sync now'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Drafts list */}
      {status?.connected && (
        <div>
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="font-display text-xl font-semibold">Pending drafts</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Confirm or discard each transaction. Nothing is saved until you apply.
              </p>
            </div>
            {drafts.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Phase 20 — Dismiss All. Hidden by an AlertDialog confirm
                    so a stray click doesn't nuke the user's review queue. */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={applying}
                      className="gap-1.5 text-muted-foreground hover:text-foreground"
                      data-testid="gmail-dismiss-all-trigger"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Dismiss all
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="gmail-dismiss-all-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Dismiss {drafts.length} draft{drafts.length === 1 ? '' : 's'}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This won&rsquo;t delete the underlying emails — only the imported drafts here.
                        You can re-sync later to bring them back.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="gmail-dismiss-all-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDiscardAll}
                        data-testid="gmail-dismiss-all-confirm"
                      >
                        Dismiss all
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyAll}
                  disabled={applying || userCards.length === 0}
                  className="gap-1.5"
                  data-testid="gmail-apply-all-button"
                >
                  <Check className="h-3.5 w-3.5" /> Apply all ({drafts.length})
                </Button>
              </div>
            )}
          </div>

          {/* Phase 21 — disclosure banner for non-spend events
              (refunds/payments/fees/interest) detected during the last
              sync. These are NOT imported as transactions but surfaced
              here so users have visibility. */}
          {lastSync?.non_spend_events?.length > 0 && (
            <Card
              className="p-3 border-cyan-500/30 bg-cyan-500/5 flex items-start gap-3"
              data-testid="gmail-non-spend-banner"
            >
              <RefreshCw className="h-4 w-4 mt-0.5 text-cyan-600 dark:text-cyan-400 shrink-0" aria-hidden />
              <div className="text-xs leading-relaxed min-w-0 flex-1">
                <div className="font-medium text-foreground">
                  We also detected {lastSync.non_spend_events.length} non-spend event
                  {lastSync.non_spend_events.length === 1 ? '' : 's'}
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {lastSync.non_spend_events.slice(0, 3).map((e, i) => (
                    <span key={i} className="mr-2">
                      <Badge variant="outline" className="px-1 py-0 h-4 text-[10px] mr-1 capitalize">
                        {e.kind}
                      </Badge>
                      {e.amount_inr ? formatINR(e.amount_inr) : '—'}
                      {e.merchant ? ` · ${e.merchant}` : ''}
                      {i < lastSync.non_spend_events.slice(0, 3).length - 1 ? ',' : ''}
                    </span>
                  ))}
                  {lastSync.non_spend_events.length > 3 && (
                    <span className="text-muted-foreground/70">
                      +{lastSync.non_spend_events.length - 3} more
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground/70 mt-1">
                  These aren&rsquo;t imported as transactions because they don&rsquo;t represent new
                  spend — credits to your card, refunds, payments, fees, and interest.
                </div>
              </div>
            </Card>
          )}

          {drafts.length === 0 ? (
            <Card className="p-10 border-dashed grid place-items-center text-center" data-testid="gmail-no-drafts">
              <Inbox className="h-7 w-7 text-muted-foreground/60 mb-2" />
              <div className="font-display text-lg font-medium">No pending drafts</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Click <span className="text-foreground">Sync now</span> to scan your inbox for recent statement emails.
              </p>
            </Card>
          ) : (
            <div className="grid gap-3" data-testid="gmail-drafts-list">
              {drafts.map((d) => (
                <DraftRow
                  key={d.id}
                  draft={d}
                  userCards={userCards}
                  onApply={handleApply}
                  onDiscard={handleDiscard}
                  applying={applying}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
